import { DataType, FormDialogMode, useNotify, UserPermission } from '@globaleris/ui.library'
import { Chip, Grid, makeStyles, useTheme } from '@material-ui/core'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router'
import {
  EvaluateLeadRequestModel,
  LeadQueryModel,
  LeadQueryRequestModel
} from '../../../../../api/Argentia/endpoints/leads/model/leads'
import { QuotesModel } from '../../../../../api/Argentia/endpoints/quotes/model/quotes'
import { EnumDominioTarea } from '../../../../../api/oData/enums/enumDominioTarea'
import { EnumEstadoLead } from '../../../../../api/oData/enums/enumEstadoLead'
import { EnumEstadoOferta } from '../../../../../api/oData/enums/enumEstadoOferta'
import { EnumTipoLead } from '../../../../../api/oData/enums/enumTipoLead'
import { CardActionProps } from '../../../../../components/shared/cardActions/cardActions'
import { ObjectionsCard } from '../../../../../components/shared/objectionsCard/objectionsCard'
import { ButtonOption } from '../../../../../components/shared/splitButton/splitButton'
import { hasPermission } from '../../../../../helpers/permissionsHelper'
import { FormDialogDefinitionProps, useFormDialog } from '../../../../../hooks'
import { NonAuthRoutes } from '../../../../../model'
import { Resources } from '../../../../../model/RBAC/constants'
import { ObjectionTableModel } from '../../../../invoicesPage/invoicesPage.Model'
import { fetchObjectionsDescriptions } from '../../../../invoicesPage/invoicesPage.service'
import { PriceComponentsCard } from '../../../../priceComponents/components/priceComponentsCard/priceComponentsCard'
import {
  deactivateQuote,
  deleteQuote,
  fetchLeadQuotes,
  fetchAllLeadQuotes,
  publishQuoteMassiveAsync,
  reEvaluateQuote
} from '../../../../quotes/quotes.service'
import {
  mapLeadQueryArrayToParamsTable,
  mapLeadQueryToForm,
  mapResponseToLeadInfoMap,
  mapRowLeadModelToRequest,
  mapRowQuoteModelToRequest
} from '../../../leads.mapper'
import { EvaluateLeadButtonOptions, EvaluateLeadModel } from '../../../leads.model'
import { evaluateLead, getObjectionsByLeadId, queryLead } from '../../../leads.service'
import { styles } from '../../../leads.styles'
import { useConfirmationDialog } from '../../../../../hooks'
import { ConfirmationDialogs } from '../../../../../model/enums/confirmationDialogs'
import { AnnexedLeadCard } from './components/annexedLeadCard/annexedLeadCard'
import { ConsumptionCard } from './components/consumptionCard/consumptionCard'
import { LeadInfoCard } from './components/leadInfoCard/leadInfoCard'
import { QuotesCard } from './components/quotesCard/quotesCard'

interface LeadsFileGeneralTabProps {
  leadId: number
  onSubtitleMount: (leadType: EnumTipoLead) => void
  onQuotesLoad?: (hasQuotes: boolean) => void
}

export const LeadsFileGeneralTab: React.FC<LeadsFileGeneralTabProps> = ({
  leadId,
  onSubtitleMount,
  onQuotesLoad
}) => {
  const [t] = useTranslation(['globalerisLibrary', 'leads', 'enums'])
  const history = useHistory()
  const { info, error } = useNotify()
  const currentTheme = useTheme()
  const useStyles = makeStyles(styles(currentTheme))
  const classes = useStyles()
  const { openForm, closeForm, updateFormDefinition, CustomFormDialog, formProps } = useFormDialog()
  const { getConfirmation, closeConfirmation } = useConfirmationDialog()

  const [leadQuery, setLeadQuery] = useState<LeadQueryModel | undefined>()
  const [leadQuotes, setLeadQuotes] = useState<QuotesModel[]>([])
  const [draftQuotes, setDraftQuotes] = useState<QuotesModel[]>([])
  const [publishedQuotes, setPublishedQuotes] = useState<QuotesModel[]>([])
  const [historicQuotes, setHistoricQuotes] = useState<QuotesModel[]>([])
  const [inactiveQuotes, setInactiveQuotes] = useState<QuotesModel[]>([])
  const [isFetchingLeadQuotes, setIsFetchingLeadQuotes] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [leadInfoCard, setLeadInfoCard] = useState<Map<string, string>>()
  // objections
  const [objections, setObjections] = useState<ObjectionTableModel[] | undefined>(undefined)
  const [objDescriptions, setObjDescriptions] = useState<ObjectionTableModel[] | undefined>(
    undefined
  )
  const [isFetchingObjections, setIsFetchingObjections] = useState<boolean>(false)
  const [isFetchingObjectionsDescriptions, setIsFetchingObjectionsDescriptions] =
    useState<boolean>(false)

  // #region Handlers Starts

  // -------------------------- InfoCard Handlers ------------------------ //
  const editInfoHandler = () => {
    leadQuery &&
      history.push(NonAuthRoutes.LeadsEdit, {
        commodity: leadQuery.commodity,
        leadType: leadQuery.leadType,
        leadData: mapLeadQueryToForm(leadQuery),
        leadAnnexedParams: mapLeadQueryArrayToParamsTable(leadQuery.annexedLead)
      })
  }

  // ------------------------- Quotes Handlers -------------------------- //
  const handleQuoteViewDetail = (selectedRow: QuotesModel) => {
    history.push(NonAuthRoutes.Quotes, { quoteId: selectedRow.id })
  }

  const handleQuoteReEvaluation = async (selectedRow: QuotesModel) => {
    const request = mapRowQuoteModelToRequest(selectedRow)
    await reEvaluateQuote(request).then(response => {
      if (response.success) {
        info(t('leads:notifications.reEevaluateQuote.success'))
      } else {
        error(t('leads:notifications.reEevaluateQuote.error'))
      }
    })
  }

  const handleViewQuoteContracts = (selectedRow: QuotesModel) => {}

  const handleEvaluateLeadOption = (option: ButtonOption) => {
    let leadRequest: EvaluateLeadModel = { id: leadId } as EvaluateLeadModel
    switch (option.id) {
      case EvaluateLeadButtonOptions.EVALUATE_LEAD:
        leadRequest.startDate = null
        leadRequest.endDate = null
        evaluateLeadAsync(mapRowLeadModelToRequest(leadRequest))
        break
      case EvaluateLeadButtonOptions.EVALUATE_WITHIN_DATE:
        openForm({
          title: t('leads:forms.evaluateLeadToDateForm.title'),
          definition: evaluateLeadToDateFormDef,
          onClose: handleEvaluateLeadToDateClose,
          mode: FormDialogMode.ADD
        })
        break
      default:
        break
    }
  }

  const evaluateLeadAsync = async (request: EvaluateLeadRequestModel) => {
    await evaluateLead([request]).then(response => {
      if (response.success) {
        info(t('leads:notifications.evaluateLead.success'))
        getLeadData(leadId)
      } else {
        error(t('leads:notifications.evaluateLead.error'))
      }
    })
  }

  const handleEvaluateLeadToDateClose = useCallback(
    (formData?: any) => {
      if (formData) {
        const leadRequest: EvaluateLeadModel = {
          id: leadId,
          startDate: formData.startDate,
          endDate: formData.endDate
        } as EvaluateLeadModel
        evaluateLeadAsync(mapRowLeadModelToRequest(leadRequest))
      }
      closeForm()
    },
    [leadId, closeForm, evaluateLeadAsync]
  )

  const handleQuoteDeactivation = async (selectedRow: QuotesModel) => {
    await getConfirmation(ConfirmationDialogs.DEACTIVATE_QUOTE)
    await deactivateQuote(selectedRow.id, EnumEstadoOferta.Baja).then(response => {
      if (response.success && response.data) {
        info(t('leads:notifications.deactivateQuote.success'))
        getLeadQuotes(leadId)
      } else {
        if (response.error?.code === '409') {
          error(t('leads:notifications.deactivateQuote.hasContractsError'))
        } else {
          error(t('leads:notifications.deactivateQuote.error'))
        }
      }
    })
    closeConfirmation()
  }

  const handleMoveToHistoric = async (selectedRow: QuotesModel) => {
    await getConfirmation(ConfirmationDialogs.MOVE_QUOTE_TO_HISTORIC)
    await deactivateQuote(selectedRow.id, EnumEstadoOferta.Historica).then(response => {
      if (response.success && response.data) {
        info(t('leads:notifications.moveToHistoric.success'))
        getLeadQuotes(leadId)
      } else {
        error(t('leads:notifications.moveToHistoric.error'))
      }
    })
    closeConfirmation()
  }

  const handleQuoteDeletion = async (selectedRow: QuotesModel) => {
    await getConfirmation(ConfirmationDialogs.DELETE_QUOTE)
    await deleteQuote(selectedRow.id).then(response => {
      if (response.success) {
        info(t('leads:notifications.deleteQuote.success'))
        getLeadQuotes(leadId)
      } else {
        error(t('leads:notifications.deleteQuote.error'))
      }
    })
    closeConfirmation()
  }

  const handlePublishLead = async () => {
    await getConfirmation(ConfirmationDialogs.PUBLISH_LEAD)
    await publishQuoteMassiveAsync([leadId]).then(response => {
      if (response.success) {
        info(t('leads:notifications.publishQuote.success'))
        Promise.all([getLeadData(leadId), getLeadQuotes(leadId)])
      } else {
        error(t('leads:notifications.publishQuote.error'))
      }
    })
    closeConfirmation()
  }

  // ------------------------- Consumption Handlers -------------------------- //
  const handleAssociateConsumption = () => {
    history.push(NonAuthRoutes.LeadsProfileDetail, {
      leadId: leadId
    })
  }

  const handleViewReport = () => {
    history.push(NonAuthRoutes.LeadsReport, {
      leadId: leadId,
      commodity: leadQuery?.commodity
    })
  }
  // #endregion Handlers Ends

  // #region Functions Starts
  // #endregion Functions Ends

  // #region Form Definition Starts

  const evaluateLeadToDateFormDef: FormDialogDefinitionProps = useMemo(() => {
    return {
      id: 'evaluateLeadToDateForm',
      sections: [
        {
          inputs: [
            {
              name: 'startDate',
              label: t('leads:forms.evaluateLeadToDateForm.inputs.startDate'),
              type: DataType.DATE
            },
            {
              name: 'endDate',
              label: t('leads:forms.evaluateLeadToDateForm.inputs.endDate'),
              type: DataType.DATE
            }
          ]
        }
      ]
    }
  }, [])

  useEffect(() => {
    updateFormDefinition(evaluateLeadToDateFormDef)
  }, [evaluateLeadToDateFormDef])

  // #endregion Form Definition Ends

  // #region DataTable Config Starts
  // #endregion DataTable Config Ends

  // #region Card Definition Starts
  const cardInfoFooterActions: CardActionProps[] = [
    {
      label: t('leads:components.leadFile.cardInfo.footerActions.edit'),
      handleActionClick: editInfoHandler,
      hasPermission: hasPermission(Resources.Leads, [UserPermission.EDIT])
    }
  ]
  // #endregion Card Definition Ends

  // #region Hooks Starts

  const getLeadQuotes = useCallback((leadId: number) => {
    setIsFetchingLeadQuotes(true)
    return fetchAllLeadQuotes(leadId)
      .then(response => {
        if (response.success && response.data) {
          setLeadQuotes(response.data)
          setDraftQuotes(response.data.filter(quote => quote.state === EnumEstadoOferta.Borrador))
          setPublishedQuotes(
            response.data.filter(quote => quote.state === EnumEstadoOferta.Publicada)
          )
          setHistoricQuotes(
            response.data.filter(quote => quote.state === EnumEstadoOferta.Historica)
          )
          setInactiveQuotes(response.data.filter(quote => quote.state === EnumEstadoOferta.Baja))
          onQuotesLoad?.(response.data.length > 0)
        } else {
          error(t('leads:notifications.getLeadQuotes.error'))
          onQuotesLoad?.(false)
        }
      })
      .finally(() => {
        setIsFetchingLeadQuotes(false)
      })
  }, [])

  const getLeadData = useCallback(
    (leadId: number) => {
      const request: LeadQueryRequestModel = {
        leadId: leadId
      }
      return queryLead(request).then(response => {
        if (response.success && response.data && response.data.length > 0) {
          setLeadQuery(response.data[0])
          setLeadInfoCard(new Map(Object.entries(mapResponseToLeadInfoMap(response.data[0]))))
          onSubtitleMount(response.data[0].leadType)
        } else {
          error(t('leads:notifications.queryLead.error'))
        }
      })
    },
    [onSubtitleMount]
  )

  useEffect(() => {
    const getLeadObjections = leadId => {
      setIsFetchingObjections(true)
      return getObjectionsByLeadId(leadId)
        .then(response => {
          if (response.success && response.data) {
            setObjections(response.data)
          } else {
            error(t('leads:notifications.getObjectionsError'))
          }
        })
        .finally(() => {
          setIsFetchingObjections(false)
        })
    }

    const getObjectionsDescriptions = () => {
      setIsFetchingObjectionsDescriptions(true)
      return fetchObjectionsDescriptions(EnumDominioTarea.Lead)
        .then(response => {
          setObjDescriptions(response.data)
        })
        .finally(() => {
          setIsFetchingObjectionsDescriptions(false)
        })
    }

    const fetchLeadData = async () => {
      setIsLoading(true)
      await Promise.all([
        getLeadData(leadId),
        getLeadQuotes(leadId),
        getLeadObjections(leadId),
        getObjectionsDescriptions()
      ]).finally(() => {
        setIsLoading(false)
      })
    }
    leadId && fetchLeadData()
  }, [])
  // #endregion Hooks Ends

  return (
    <Grid container spacing={4} alignItems="flex-start">
      <Grid item container spacing={2} xs={12} md={6} lg={5}>
        <LeadInfoCard
          className={classes.leadFileCard}
          isLoading={isLoading}
          withTransition={true}
          info={leadInfoCard}
          title={leadQuery?.code}
          subtitle={leadQuery?.description}
          footerActions={cardInfoFooterActions}
          headerActions={
            leadQuery?.leadState !== undefined ? (
              <Chip
                size="small"
                label={t(`enums:EnumEstadoLead.${EnumEstadoLead[leadQuery.leadState]}`)}
                className={`${classes.leadStateChip} ${
                  leadQuery.leadState === EnumEstadoLead.SinEvaluar
                    ? classes.leadStateChipSinEvaluar
                    : leadQuery.leadState === EnumEstadoLead.EvaluacionSolicitada
                      ? classes.leadStateChipEvaluacionSolicitada
                      : leadQuery.leadState === EnumEstadoLead.Procesando
                        ? classes.leadStateChipProcesando
                        : leadQuery.leadState === EnumEstadoLead.Evaluado
                          ? classes.leadStateChipEvaluado
                          : leadQuery.leadState === EnumEstadoLead.EvaluacionErronea
                            ? classes.leadStateChipEvaluacionErronea
                            : classes.leadStateChipPublicado
                }`}
              />
            ) : undefined
          }
        />

        <AnnexedLeadCard
          className={classes.leadFileCard}
          isLoading={isLoading}
          tableData={leadQuery?.annexedLead}
          footerActions={cardInfoFooterActions}
        />
        {leadQuery?.leadType !== EnumTipoLead.Precios && (
          <ConsumptionCard
            className={classes.leadFileCard}
            isLoading={isLoading}
            onAssociateConsumptionClick={handleAssociateConsumption}
          />
        )}
      </Grid>
      <Grid item container spacing={2} xs={12} md={6} lg={7}>
        {leadQuery?.leadType !== EnumTipoLead.Precios && (
          <>
            <QuotesCard
              className={classes.leadFileCard}
              isLoading={isFetchingLeadQuotes}
              draftQuotes={draftQuotes}
              publishedQuotes={publishedQuotes}
              historicQuotes={historicQuotes}
              inactiveQuotes={inactiveQuotes}
              leadType={leadQuery?.leadType}
              leadState={leadQuery?.leadState}
              onViewDetailClick={handleQuoteViewDetail}
              onReEvaluateQuoteClick={handleQuoteReEvaluation}
              onEvaluateLeadClick={handleEvaluateLeadOption}
              onDeactivateClick={handleQuoteDeactivation}
              onMoveToHistoricClick={handleMoveToHistoric}
              onDeleteClick={handleQuoteDeletion}
              onPublishLeadClick={handlePublishLead}
              onViewReportClick={handleViewReport}
            />
          </>
        )}
        <PriceComponentsCard
          style={{ marginBottom: '0px' }}
          className={classes.leadFileCard}
          leadId={leadId}
          commodityId={leadQuery?.commodity}
          onRetry={() => {}}
          insideTooltip
        />

        <ObjectionsCard
          className={classes.leadFileCard}
          isLoading={isFetchingObjections || isFetchingObjectionsDescriptions}
          objectionsData={objections}
          allowInsert={false}
          objDescriptions={objDescriptions ?? []}
          showDescription={false}
          showState={false}
          showIdObjMaestra={false}
          hideTitle={false}
          groupByCode={true}
          // onRowDelete={handleDeleteObjection}
          // hasEditPermission={
          //   hasPermission(Resources.Leads, [UserPermission.EDIT])
          // }
          // tableUserPermissions={getResourcesUserPermissions([
          //   Resources.Leads
          // ])}
        />
        <CustomFormDialog {...formProps} />
      </Grid>
    </Grid>
  )
}
